import { Action } from '@ngrx/store';
import {
  ScenarioInterface,
  ScenarioItemSaveInterface,
  ScenarioAdvancedFilterAddRequestInterface,
  ISchedulerKpiMetricInfo,
  IKpiCardDataProperties,
  IUpdateSchedulerOptimization,
  ICheckForDeployResponse,
  IScenarioUncreatedWorkOrder,
  IScenarioGanttAllItemsResponse,
  ILinePathDetailsBySiteId,
  ISchedulerListViewDownloadExcelPayload,
  ISaveManySchedulerWorkOrderRules,
  ISchedulerShift,
  IOptimizationAlgorithms,
  ScenarioItemInterface,
  IScenarioItemsLoadOptions,
  ISchedulerOptimizationResponse,
} from './scheduler.model';
import { IProductFamily } from '../settings/product-family/product-family.model';
import {
  ScenarioItemsDeployRequestInterface,
  SchedulerScenarioHorizonInterface,
} from '../../shared/service/scheduler/scheduler-service.model';
import { SchedulerScenarioActionTypes } from '../scheduler-scenario/scheduler-scenario.actions';
import { ActivitiesInterface } from '../../shared/model/interface/activities.model';
import {
  BaseCrudResponse,
  BaseOneResponseInterface,
  BulkResponseDataInterface,
  GetManyResponseInterface,
} from '../../shared/model/interface/crud-response-interface.model';
import {
  IEditScenarioRequest,
  ScenarioBulkResponseDataInterface,
} from '../scheduler-scenario/scheduler-scenario.model';
import {
  ICheckForDeployItemsRequest,
  ICheckForIncompatibleWorkOrdersRequest,
  ICheckForIncompatibleWorkOrdersResponse,
} from '../../view/scheduler/production-gantt-view/production-gantt-view.component.model';
import {
  IWorkOrderScheduleWithRules,
  TFetchWorkOrdersForSchedulerRules,
} from '../scheduler-work-order-set/scheduler-work-order-set.model';

export enum SchedulerActionTypes {
  LoadScenario = '[Scheduler] Load Scenario',
  ScenarioLoaded = '[Scheduler] Scenario Loaded',
  LoadScenarioItems = '[Scheduler] Load Scenario Items',
  ScenarioItemsLoaded = '[Scheduler] Scenario Items Loaded',
  SaveScenarioItems = '[Scheduler] Save Scenario Items',
  ScenarioItemsSaved = '[Scheduler] Scenario Items Saved',
  DeployScenarioItems = '[Scheduler] Deploy Scenario Items',
  ScenarioItemsDeployed = '[Scheduler] Scenario Items Deployed',
  HideShowApplyButton = '[Scheduler] Hide Show Apply Button',
  LoadUnplannedWorkOrderData = '[Scheduler] Load Unplanned Work Orders Data',
  UnplannedWorkOrderDataLoaded = '[Scheduler] Unplanned Work Orders Data Loaded',
  LoadDownTimeActivitiesData = '[Scheduler] Load Down Time Activities Data',
  DownTimeActivitiesDataLoaded = '[Scheduler] Down Time Activities Data Loaded',
  ClearSchedulerData = '[Scheduler] Clear Scheduler Unplanned Data',
  GanttUnsavedChangeStatus = '[Scheduler] Gantt Unsaved Change Status',
  FetchError = '[Scheduler] FetchError',
  LoadScenarioShiftPlans = '[Scheduler] Load Scenario Plans',
  ScenarioShiftPlansLoaded = '[Scheduler] Scenario Plans Loaded',
  SaveManyScenarioWorkOrderRules = '[Scheduler] Save Many Scenario Work Order Rules',
  ScenarioWorkOrderRulesAdded = '[Scheduler] Scenario Work Order Rules Added',
  AddScenarioAdvancedFilter = '[Scheduler] Add Scenario Advanced Filter',
  ScenarioAdvancedFilterAdded = '[Scheduler] Scenario Advanced Filter Added',
  KpiMetricInfoLoad = '[Scheduler] Kpi Metric Info Load',
  KpiMetricInfoLoaded = '[Scheduler] Kpi Metric Info Loaded',
  SaveSchedulerKpiCardConfigs = '[Scheduler] Save Scheduler Kpi Card Configs',
  SaveSchedulerKpiCardData = '[Scheduler] Save Scheduler Kpi Card Data',
  PartialUpdateKpiCardData = '[Scheduler] Partial Update Kpi Card Data',
  OptimizeScheduler = '[Scheduler] Optimize Scheduler',
  SchedulerOptimized = '[Scheduler] Scheduler Optimized',
  LoadSchedulerOptimizations = '[Scheduler] Load Scheduler Optimizations',
  SchedulerOptimizationsLoaded = '[Scheduler] Scheduler Optimizations Loaded',
  UpdateSchedulerOptimization = '[Scheduler] Update Scheduler Optimization ',
  SchedulerOptimizationUpdated = '[Scheduler] Scheduler Optimization Updated',
  OptimizationSelectCurrentPlan = '[Scheduler] Optimization Select Current Plan',
  OptimizationSelectCurrentPlanCompleted = '[Scheduler] Optimization Select Current Plan Completed',
  SetViewStatus = '[Scheduler] Set View Status',
  ShowSchedulerOptimizationMode = '[Scheduler] Show Scheduler Optimization Mode',
  OnHoldSaveKpiCardData = '[Scheduler] On Hold Save Kpi Card Data',
  ClearInitialSchedulerData = '[Scheduler] Clear Initial Scheduler Data',
  CheckForDeployItems = '[Scheduler] Check For Deploy Items',
  CheckForDeployItemsCompleted = '[Scheduler] Check For Deploy Items Completed',
  CheckForIncompatibleWorkOrders = '[Scheduler] Check For Incompatible Work Orders',
  CheckForIncompatibleWorkOrdersCompleted = '[Scheduler] Check For Incompatible Work Orders Completed',
  SaveScenarioUncreatedWorkOrders = '[Scheduler] Save Scenario Uncreated Work Orders',
  ScenarioUncreatedWorkOrdersSaved = '[Scheduler] Scenario Uncreated Work Orders Saved',
  DeleteScenarioUncreatedWorkOrders = '[Scheduler] Delete Scenario Uncreated Work Orders',
  ScenarioUncreatedWorkOrdersDeleted = '[Scheduler] Scenario Uncreated Work Orders Deleted',
  LoadLinePathData = '[Scheduler] Load Line Path Data',
  LinePathDataLoaded = '[Scheduler] Line Path Data Loaded',
  SchedulerKpiCardDataSaved = '[Scheduler] Scheduler Kpi Card Data Saved',
  SchedulerDownloadExcel = '[Scheduler] Scheduler Download Excel',
  SchedulerDownloadExcelCompleted = '[Scheduler] Scheduler Download Excel Completed',
  LoadOptimizationAlgorithms = '[Scheduler] Load Optimization Algorithms',
  OptimizationAlgorithmsLoaded = '[Scheduler] Optimization Algorithms Loaded',
  UpdateSchedulerScenario = '[Scheduler] Update Scheduler Scenario',
  SchedulerScenarioUpdated = '[Scheduler] Scheduler Scenario Updated',
  SaveSelectedOptimizationItems = '[Scheduler] Save Selected Optimization Items',
  SelectedOptimizationItemsSaved = '[Scheduler] Selected Optimization Items Saved',
  CheckSchedulerOptimizationsExist = '[Scheduler] Check Scheduler Optimizations Exist',
  SchedulerOptimizationsExistChecked = '[Scheduler] Scheduler Optimizations Exits Checked',
  LoadSchedulerOptimizationsStatus = '[Scheduler] Load Scheduler Optimizations Status',
  SchedulerOptimizationsStatusLoaded = '[Scheduler] Scheduler Optimizations Status Loaded',
  LoadProductFamilyColors = '[Scheduler] Load Product Family Colors',
  ProductFamilyColorsLoaded = '[Scheduler] Product Family Colors Loaded',
}

export class LoadScenario implements Action {
  readonly type = SchedulerActionTypes.LoadScenario;

  constructor(public scenarioId: number, public isKpiMetricInfoLoad: boolean = true) {}
}

export class ScenarioLoaded implements Action {
  readonly type = SchedulerActionTypes.ScenarioLoaded;

  constructor(public payload: BaseOneResponseInterface<ScenarioInterface>) {}
}

export class LoadScenarioItems implements Action {
  readonly type = SchedulerActionTypes.LoadScenarioItems;

  constructor(
    public scenario: ScenarioInterface,
    public startDate: string = null,
    public endDate: string = null,
    public loadOptions?: IScenarioItemsLoadOptions,
  ) {}
}

export class ScenarioItemsLoaded implements Action {
  readonly type = SchedulerActionTypes.ScenarioItemsLoaded;

  constructor(public payload: IScenarioGanttAllItemsResponse) {}
}

export class SaveScenarioItems implements Action {
  readonly type = SchedulerActionTypes.SaveScenarioItems;

  constructor(
    public scenarioId: number,
    public scenarioItems: ScenarioItemSaveInterface[],
    public startDate: string,
    public endDate: string,
    public triggeredByKPI: boolean = false,
    public linesToDeploy: number[] | null = null,
  ) {}
}

export class ScenarioItemsSaved implements Action {
  readonly type = SchedulerActionTypes.ScenarioItemsSaved;

  constructor(
    public payload: GetManyResponseInterface<ScenarioItemInterface>,
    public scenarioItemsMetaData: {
      scenarioId: number;
      scenarioItems: ScenarioItemSaveInterface[];
      triggeredByKPI: boolean;
      linesToDeploy: number[] | null;
    },
  ) {}
}

export class DeployScenarioItems implements Action {
  readonly type = SchedulerActionTypes.DeployScenarioItems;

  constructor(
    public horizon: SchedulerScenarioHorizonInterface,
    public deployedItems: ScenarioItemsDeployRequestInterface[],
    public siteId: number,
    public uncreatedWorkOrders?: IScenarioUncreatedWorkOrder[],
  ) {}
}

export class ScenarioItemsDeployed implements Action {
  readonly type = SchedulerActionTypes.ScenarioItemsDeployed;

  constructor(public data: BaseCrudResponse[], public success: boolean) {}
}

export class HideShowApplyButton implements Action {
  readonly type = SchedulerActionTypes.HideShowApplyButton;

  constructor(public payload: boolean = true) {}
}

export class LoadUnplannedWorkOrderData implements Action {
  readonly type = SchedulerActionTypes.LoadUnplannedWorkOrderData;

  constructor(public filters: TFetchWorkOrdersForSchedulerRules) {}
}

export class UnplannedWorkOrderDataLoaded implements Action {
  readonly type = SchedulerActionTypes.UnplannedWorkOrderDataLoaded;

  constructor(public payload: IWorkOrderScheduleWithRules[]) {}
}

export class LoadDownTimeActivitiesData implements Action {
  readonly type = SchedulerActionTypes.LoadDownTimeActivitiesData;

  constructor(public scenarioId: number) {}
}

export class DownTimeActivitiesDataLoaded implements Action {
  readonly type = SchedulerActionTypes.DownTimeActivitiesDataLoaded;

  constructor(public payload: ActivitiesInterface[]) {}
}

export class ClearSchedulerData implements Action {
  readonly type = SchedulerActionTypes.ClearSchedulerData;
}

export class GanttUnsavedChangeStatus implements Action {
  readonly type = SchedulerActionTypes.GanttUnsavedChangeStatus;

  constructor(public isUnsavedChangeInGantt: boolean = false) {}
}

export class FetchError implements Action {
  readonly type = SchedulerScenarioActionTypes.FetchError;

  constructor(public payload: any) {}
}

export class LoadScenarioShiftPlans implements Action {
  readonly type = SchedulerActionTypes.LoadScenarioShiftPlans;

  constructor(public scenarioId: number) {}
}

export class ScenarioShiftPlansLoaded implements Action {
  readonly type = SchedulerActionTypes.ScenarioShiftPlansLoaded;

  constructor(public payload: ISchedulerShift[]) {}
}

export class SaveManyScenarioWorkOrderRules implements Action {
  readonly type = SchedulerActionTypes.SaveManyScenarioWorkOrderRules;

  constructor(public payload: ISaveManySchedulerWorkOrderRules) {}
}

export class ScenarioWorkOrderRulesAdded implements Action {
  readonly type = SchedulerActionTypes.ScenarioWorkOrderRulesAdded;

  constructor(public payload: ScenarioBulkResponseDataInterface) {}
}

export class AddScenarioAdvancedFilter implements Action {
  readonly type = SchedulerActionTypes.AddScenarioAdvancedFilter;

  constructor(public advancedFilter: ScenarioAdvancedFilterAddRequestInterface) {}
}

export class ScenarioAdvancedFilterAdded implements Action {
  readonly type = SchedulerActionTypes.ScenarioAdvancedFilterAdded;

  constructor(public payload: BaseOneResponseInterface<ScenarioInterface>) {}
}

export class KpiMetricInfoLoad implements Action {
  readonly type = SchedulerActionTypes.KpiMetricInfoLoad;

  constructor(public scenarioId: number) {}
}

export class KpiMetricInfoLoaded implements Action {
  readonly type = SchedulerActionTypes.KpiMetricInfoLoaded;

  constructor(public payload: ISchedulerKpiMetricInfo) {}
}

export class SaveSchedulerKpiCardConfigs implements Action {
  readonly type = SchedulerActionTypes.SaveSchedulerKpiCardConfigs;

  constructor(public payload: any) {}
}

export class SaveSchedulerKpiCardData implements Action {
  readonly type = SchedulerActionTypes.SaveSchedulerKpiCardData;

  constructor(
    public payload: {
      data: IKpiCardDataProperties;
      scenarioId: number;
    },
  ) {}
}

export class PartialUpdateKpiCardData implements Action {
  readonly type = SchedulerActionTypes.PartialUpdateKpiCardData;

  constructor(
    public payload: {
      component: string;
      properties: IKpiCardDataProperties;
    },
  ) {}
}

export class SetViewStatus implements Action {
  readonly type = SchedulerActionTypes.SetViewStatus;

  constructor(public viewStatus: string) {}
}

export class OptimizeScheduler implements Action {
  readonly type = SchedulerActionTypes.OptimizeScheduler;

  constructor(public scenarioId: number, public optimizationType: string) {}
}

export class SchedulerOptimized implements Action {
  readonly type = SchedulerActionTypes.SchedulerOptimized;

  constructor(public payload: ISchedulerOptimizationResponse) {}
}

export class LoadSchedulerOptimizations implements Action {
  readonly type = SchedulerActionTypes.LoadSchedulerOptimizations;

  constructor(public scenarioId: number) {}
}

export class SchedulerOptimizationsLoaded implements Action {
  readonly type = SchedulerActionTypes.SchedulerOptimizationsLoaded;

  constructor(public payload: ISchedulerOptimizationResponse[]) {}
}

export class CheckSchedulerOptimizationsExist implements Action {
  readonly type = SchedulerActionTypes.CheckSchedulerOptimizationsExist;

  constructor(public scenarioId: number) {}
}

export class SchedulerOptimizationsExistChecked implements Action {
  readonly type = SchedulerActionTypes.SchedulerOptimizationsExistChecked;

  constructor(public exists: boolean) {}
}

export class LoadSchedulerOptimizationsStatus implements Action {
  readonly type = SchedulerActionTypes.LoadSchedulerOptimizationsStatus;

  constructor(public scenarioId: number) {}
}

export class SchedulerOptimizationsStatusLoaded implements Action {
  readonly type = SchedulerActionTypes.SchedulerOptimizationsStatusLoaded;

  constructor(public payload: { status: number; optimizationType: string }[]) {}
}

export class UpdateSchedulerOptimization implements Action {
  readonly type = SchedulerActionTypes.UpdateSchedulerOptimization;

  constructor(public scenarioId: number, public params: IUpdateSchedulerOptimization) {}
}

export class SchedulerOptimizationUpdated implements Action {
  readonly type = SchedulerActionTypes.SchedulerOptimizationUpdated;

  constructor(public payload: ISchedulerOptimizationResponse) {}
}

export class OptimizationSelectCurrentPlan implements Action {
  readonly type = SchedulerActionTypes.OptimizationSelectCurrentPlan;

  constructor(public scenarioId: number) {}
}

export class OptimizationSelectCurrentPlanCompleted implements Action {
  readonly type = SchedulerActionTypes.OptimizationSelectCurrentPlanCompleted;

  constructor(public payload: boolean) {}
}

export class LoadProductFamilyColors implements Action {
  readonly type = SchedulerActionTypes.LoadProductFamilyColors;

  constructor(public siteId: number) {}
}

export class ProductFamilyColorsLoaded implements Action {
  readonly type = SchedulerActionTypes.ProductFamilyColorsLoaded;

  constructor(public productFamilies: IProductFamily[]) {}
}

export class ShowSchedulerOptimizationMode implements Action {
  readonly type = SchedulerActionTypes.ShowSchedulerOptimizationMode;

  constructor(public isShowSchedulerOptimizationMode: boolean) {}
}

export class OnHoldSaveKpiCardData implements Action {
  readonly type = SchedulerActionTypes.OnHoldSaveKpiCardData;

  constructor(public isOnHoldSaveKpiCardData: boolean) {}
}

export class ClearInitialSchedulerData implements Action {
  readonly type = SchedulerActionTypes.ClearInitialSchedulerData;
}

export class CheckForDeployItems implements Action {
  readonly type = SchedulerActionTypes.CheckForDeployItems;

  constructor(
    public checkForDeployItems: ICheckForDeployItemsRequest[],
    public uncreatedWorkOrders: IScenarioUncreatedWorkOrder[],
  ) {}
}

export class CheckForDeployItemsCompleted implements Action {
  readonly type = SchedulerActionTypes.CheckForDeployItemsCompleted;

  constructor(public payload: ICheckForDeployResponse) {}
}

export class CheckForIncompatibleWorkOrders implements Action {
  readonly type = SchedulerActionTypes.CheckForIncompatibleWorkOrders;

  constructor(public params: ICheckForIncompatibleWorkOrdersRequest) {}
}

export class CheckForIncompatibleWorkOrdersCompleted implements Action {
  readonly type = SchedulerActionTypes.CheckForIncompatibleWorkOrdersCompleted;

  constructor(public payload: GetManyResponseInterface<ICheckForIncompatibleWorkOrdersResponse>) {}
}

export class SaveScenarioUncreatedWorkOrders implements Action {
  readonly type = SchedulerActionTypes.SaveScenarioUncreatedWorkOrders;

  constructor(public scenarioId: number, public uncreatedWorkOrders: IScenarioUncreatedWorkOrder[]) {}
}

export class ScenarioUncreatedWorkOrdersSaved implements Action {
  readonly type = SchedulerActionTypes.ScenarioUncreatedWorkOrdersSaved;

  constructor(public payload: ScenarioBulkResponseDataInterface) {}
}

export class DeleteScenarioUncreatedWorkOrders implements Action {
  readonly type = SchedulerActionTypes.DeleteScenarioUncreatedWorkOrders;

  constructor(public uncreatedWorkOrderIds: number[]) {}
}

export class ScenarioUncreatedWorkOrdersDeleted implements Action {
  readonly type = SchedulerActionTypes.ScenarioUncreatedWorkOrdersDeleted;

  constructor(public payload: BulkResponseDataInterface) {}
}

export class SchedulerKpiCardDataSaved implements Action {
  readonly type = SchedulerActionTypes.SchedulerKpiCardDataSaved;

  constructor(public payload: BaseOneResponseInterface<ScenarioInterface>) {}
}

export class LoadLinePathData implements Action {
  readonly type = SchedulerActionTypes.LoadLinePathData;

  constructor(public siteId: number) {}
}

export class LinePathDataLoaded implements Action {
  readonly type = SchedulerActionTypes.LinePathDataLoaded;

  constructor(public payload: GetManyResponseInterface<ILinePathDetailsBySiteId>) {}
}

export class SchedulerListViewDownloadExcel implements Action {
  readonly type = SchedulerActionTypes.SchedulerDownloadExcel;

  constructor(public payload: ISchedulerListViewDownloadExcelPayload) {}
}

export class SchedulerListViewDownloadExcelCompleted implements Action {
  readonly type = SchedulerActionTypes.SchedulerDownloadExcelCompleted;
}

export class LoadOptimizationAlgorithms implements Action {
  readonly type = SchedulerActionTypes.LoadOptimizationAlgorithms;
}

export class OptimizationAlgorithmsLoaded implements Action {
  readonly type = SchedulerActionTypes.OptimizationAlgorithmsLoaded;

  constructor(public response: IOptimizationAlgorithms[]) {}
}

export class UpdateSchedulerScenario implements Action {
  readonly type = SchedulerActionTypes.UpdateSchedulerScenario;

  constructor(public id: number, public scenario: IEditScenarioRequest, public isProductionScheduler: boolean = true) {}
}

export class SchedulerScenarioUpdated implements Action {
  readonly type = SchedulerActionTypes.SchedulerScenarioUpdated;

  constructor(public payload: BaseOneResponseInterface<ScenarioInterface>) {}
}

export class SaveSelectedOptimizationItems implements Action {
  readonly type = SchedulerActionTypes.SaveSelectedOptimizationItems;

  constructor(
    public scenarioId: number,
    public scenarioItems: ScenarioItemSaveInterface[],
    public startDate: string,
    public endDate: string,
  ) {}
}

export class SelectedOptimizationItemsSaved implements Action {
  readonly type = SchedulerActionTypes.SelectedOptimizationItemsSaved;

  constructor(public payload: GetManyResponseInterface<ScenarioItemInterface>) {}
}

export type SchedulerActions =
  | LoadScenario
  | ScenarioLoaded
  | LoadScenarioItems
  | ScenarioItemsLoaded
  | SaveScenarioItems
  | ScenarioItemsSaved
  | DeployScenarioItems
  | ScenarioItemsDeployed
  | HideShowApplyButton
  | LoadUnplannedWorkOrderData
  | LoadDownTimeActivitiesData
  | UnplannedWorkOrderDataLoaded
  | DownTimeActivitiesDataLoaded
  | ClearSchedulerData
  | GanttUnsavedChangeStatus
  | LoadScenarioShiftPlans
  | ScenarioShiftPlansLoaded
  | SaveManyScenarioWorkOrderRules
  | ScenarioWorkOrderRulesAdded
  | AddScenarioAdvancedFilter
  | ScenarioAdvancedFilterAdded
  | KpiMetricInfoLoad
  | KpiMetricInfoLoaded
  | SaveSchedulerKpiCardConfigs
  | SaveSchedulerKpiCardData
  | PartialUpdateKpiCardData
  | OptimizeScheduler
  | SchedulerOptimized
  | LoadSchedulerOptimizations
  | SchedulerOptimizationsLoaded
  | UpdateSchedulerOptimization
  | SchedulerOptimizationUpdated
  | ShowSchedulerOptimizationMode
  | OnHoldSaveKpiCardData
  | ClearInitialSchedulerData
  | CheckForDeployItems
  | CheckForDeployItemsCompleted
  | CheckForIncompatibleWorkOrders
  | CheckForIncompatibleWorkOrdersCompleted
  | SaveScenarioUncreatedWorkOrders
  | ScenarioUncreatedWorkOrdersSaved
  | DeleteScenarioUncreatedWorkOrders
  | ScenarioUncreatedWorkOrdersDeleted
  | LoadLinePathData
  | LinePathDataLoaded
  | FetchError
  | SetViewStatus
  | SchedulerKpiCardDataSaved
  | SchedulerListViewDownloadExcel
  | LoadOptimizationAlgorithms
  | OptimizationAlgorithmsLoaded
  | SchedulerListViewDownloadExcelCompleted
  | UpdateSchedulerScenario
  | SchedulerScenarioUpdated
  | SaveSelectedOptimizationItems
  | SelectedOptimizationItemsSaved
  | LoadSchedulerOptimizationsStatus
  | SchedulerOptimizationsStatusLoaded
  | CheckSchedulerOptimizationsExist
  | SchedulerOptimizationsExistChecked
  | OptimizationSelectCurrentPlan
  | OptimizationSelectCurrentPlanCompleted
  | LoadProductFamilyColors
  | ProductFamilyColorsLoaded;
